'use client';
import { useState, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import {
  MyEquipmentTab,
  MyEquipmentTabPanel,
  MyEquipmentSerialInput,
  MyEquipmentCatIconSuccess,
  Conditional,
  MyEquipmentSearchResults
} from 'components/pcc-components';
import { CatLoadingIndicator } from 'components/blocks-components';
import {
  EQUIPMENT_SEARCH_FILTER,
  SELECTED_EQUIPMENT,
  FORM_FIELD_SERIAL,
  SEARCH_LENGTH,
  SEARCH_RESULT_LIMIT,
  WIDGET_IDENTIFIER,
  MEQ_SERIAL_NO_MATCH,
  NOTIFY_DEALER,
  DUPLICATE_EQUIPMENT_ERROR
} from '../../../constants';
import { SerialNumberHelpDrawer } from '../serial-number-help-drawer/serial-number-help-drawer';
import { useFormContext } from 'react-hook-form';
import { SelectedEquipmentInfo } from 'components/my-equipment/selected-equipment/selected-equipment-info';
import { validateSerialNumber } from './utils';
import { UseEquipmentSuggestionsResult } from 'components/my-equipment/types';
import { useEquipmentSearchSuggestions } from 'components/my-equipment/hooks/use-equipment-search-suggestions';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import styles from '../style.module.scss';
import { AddEquipmentDuplicateBanner } from '../../add-equipment-duplicate-banner';
import { useFeedback } from 'providers/feedback-provider';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import {
  GA_FORM_NAME_BY_SERIAL,
  GA_LOCATION_FOR_MEQ,
  STATUS_FAIL,
  STATUS_SUCCESS
} from 'constants/analyticsConstants';
import { AddEquipmentProps } from '../types';

export const AddEquipmentBySerial = ({
  setShowSearchSuggestionFailureBanner,
  handleYesClickedDuplicateBanner,
  showBackButton,
  isChild,
  formLocation
}: Readonly<AddEquipmentProps>) => {
  const t = useTranslations();
  const textMEQSerialNoMatch = t(MEQ_SERIAL_NO_MATCH);
  const [errorText, setErrorText] = useState<string>('');
  const [displaySelectedEquipment, setDisplaySelectedEquipment] =
    useState(false);
  const { fireEvent } = useFormSubmitEvent();
  const {
    trigger,
    getFieldState,
    getValues,
    setValue,
    watch,
    resetField,
    clearErrors,
    reset
  } = useFormContext();
  const watchBySerialField = watch(FORM_FIELD_SERIAL);
  const { dismissFeedback, showFeedback, feedbackMessage } = useFeedback();
  const showDuplicateEquipmentBanner =
    showFeedback && feedbackMessage === DUPLICATE_EQUIPMENT_ERROR;
  const {
    clearSuggestions,
    getEquipmentSuggestions,
    genericError,
    error,
    isFetching,
    suggestions,
    status
  }: UseEquipmentSuggestionsResult = useEquipmentSearchSuggestions();
  const displaySuggestionBox =
    suggestions?.equipments.length > 1 &&
    isEmpty(getValues(SELECTED_EQUIPMENT));
  const isErrorEnabled =
    errorText || showDuplicateEquipmentBanner ? true : null;
  const displaySelectedEquipmentInfo =
    displaySelectedEquipment &&
    getValues(SELECTED_EQUIPMENT) !== null &&
    !isErrorEnabled;
  const showSuccessIcon =
    !isEmpty(suggestions?.equipments) &&
    !isFetching &&
    !showDuplicateEquipmentBanner;
  const formLocationText = formLocation || GA_LOCATION_FOR_MEQ;

  const handleSearch = (value: string): void => {
    setDisplaySelectedEquipment(false);
    if (value.length < 2) return;
    getEquipmentSuggestions({
      searchTerm: value,
      filter: EQUIPMENT_SEARCH_FILTER.SERIAL
    });
  };

  const serialSearch = (str: string): void => {
    dismissFeedback();
    setErrorText('');
    const validateSearch = async (str: string) => {
      await trigger(FORM_FIELD_SERIAL);
      if (!getFieldState(FORM_FIELD_SERIAL).error) {
        handleSearch(str);
      }
    };

    if (str !== getValues(FORM_FIELD_SERIAL)) {
      clearSuggestions();
      setValue(SELECTED_EQUIPMENT, null);
      validateSearch(str);
    }
  };

  const selectEquipment = useCallback(
    (value) => {
      value.serialNumber = getValues(FORM_FIELD_SERIAL);
      setValue(SELECTED_EQUIPMENT, value);
      setDisplaySelectedEquipment(true);
      clearErrors();
    },
    [clearErrors, getValues, setValue]
  );

  const handleNoClickedDuplicateBanner = () => {
    clearSuggestions();
    reset();
    setValue(NOTIFY_DEALER, false);
  };

  const showSearchSuggestionFailureBanner = useCallback(
    (value) => {
      setShowSearchSuggestionFailureBanner &&
        setShowSearchSuggestionFailureBanner(value);
    },
    [setShowSearchSuggestionFailureBanner]
  );

  useEffect(() => {
    if (isEmpty(watchBySerialField)) {
      clearSuggestions();
      resetField(FORM_FIELD_SERIAL);
      setErrorText('');
      clearErrors();
      showSearchSuggestionFailureBanner(false);
      dismissFeedback();
    }
  }, [
    clearErrors,
    clearSuggestions,
    dismissFeedback,
    resetField,
    showSearchSuggestionFailureBanner,
    watchBySerialField
  ]);

  useEffect(() => {
    if (error?.message === MEQ_SERIAL_NO_MATCH) {
      setErrorText(textMEQSerialNoMatch);
      showSearchSuggestionFailureBanner(false);
      fireEvent({
        formName: GA_FORM_NAME_BY_SERIAL,
        formStatus: STATUS_FAIL,
        formContent: getValues(FORM_FIELD_SERIAL),
        formLocation: formLocationText,
        formFieldCausingError: textMEQSerialNoMatch
      });
    } else if (error?.message) {
      showSearchSuggestionFailureBanner(status === 'error');
    } else {
      showSearchSuggestionFailureBanner(false); // sets the banner to false while success
      setErrorText(''); // clears error message when no error
    }
  }, [
    status,
    error,
    showSearchSuggestionFailureBanner,
    textMEQSerialNoMatch,
    dismissFeedback,
    fireEvent,
    getValues,
    formLocationText
  ]);

  // sets the selectedEquipment method when there is no multiple suggestions
  useEffect(() => {
    if (suggestions?.equipments?.length === 1) {
      selectEquipment(suggestions?.equipments[0]);
    }

    if (suggestions?.equipments?.length >= 1) {
      fireEvent({
        formName: GA_FORM_NAME_BY_SERIAL,
        formStatus: STATUS_SUCCESS,
        formContent: getValues(FORM_FIELD_SERIAL),
        formLocation: formLocationText
      });
    }
  }, [fireEvent, formLocationText, getValues, selectEquipment, suggestions]);

  useEffect(() => {
    if (!genericError) return;
    fireEvent({
      formName: GA_FORM_NAME_BY_SERIAL,
      formStatus: STATUS_FAIL,
      formContent: getValues(FORM_FIELD_SERIAL),
      formLocation: formLocationText
    });
    clearSuggestions();
    clearErrors();
  }, [
    clearErrors,
    clearSuggestions,
    fireEvent,
    formLocationText,
    genericError,
    getValues
  ]);

  return (
    <>
      <MyEquipmentTab tabProps={{ ariaLabelledBy: t('BY_SERIAL') }}>
        {t('BY_SERIAL')}
      </MyEquipmentTab>
      <MyEquipmentTabPanel>
        <div className="position-relative">
          <MyEquipmentSerialInput
            placeholder={t('ORDER_HISTORY_SERIALNUM_PLACEHOLDER')}
            name={FORM_FIELD_SERIAL}
            maxLength={SEARCH_LENGTH.MAX}
            onChange={serialSearch}
            fieldNote={errorText || null}
            isError={isErrorEnabled}
            rules={{
              validate: {
                validateSerialNumber: (fieldValue) => {
                  if (fieldValue.length === 0) return true;
                  return (
                    validateSerialNumber(fieldValue).isValid ||
                    t('MEQ_SERIAL_INVALID')
                  );
                }
              }
            }}
            dataCsMask
          />
          <Conditional test={displaySuggestionBox}>
            <div className={styles['serial-multi-result']}>
              <MyEquipmentSearchResults
                data-testid="equipmentList"
                list={suggestions?.equipments}
                onSelectListItem={selectEquipment}
                limit={SEARCH_RESULT_LIMIT.MATCHING}
                searchTerm={suggestions?.equipments[0].serialNumber}
                serialPrefixLabel={t('PREFIX_LABEL')}
              />
            </div>
          </Conditional>
          <MyEquipmentCatIconSuccess showIcon={showSuccessIcon} />
          <Conditional
            test={isEmpty(suggestions) && isFetching && isEmpty(errorText)}
          >
            <CatLoadingIndicator
              className={`${styles['inline-loader-container']}`}
            />
          </Conditional>
          <Conditional test={displaySelectedEquipmentInfo}>
            <SelectedEquipmentInfo
              currentEquipment={getValues(SELECTED_EQUIPMENT)}
              identifier={WIDGET_IDENTIFIER.ADD}
            />
          </Conditional>
          <Conditional test={showDuplicateEquipmentBanner}>
            <AddEquipmentDuplicateBanner
              onAccept={handleYesClickedDuplicateBanner}
              onCancel={handleNoClickedDuplicateBanner}
            />
          </Conditional>
          <SerialNumberHelpDrawer
            showBackButton={showBackButton}
            isChild={isChild}
          />
        </div>
      </MyEquipmentTabPanel>
    </>
  );
};
